import { supabase } from "@/integrations/supabase/client";
import type { ReaderAiBlock, ReaderAiMessage, ReaderAiThread, ReaderAiThreadSummary, ToofiProductMode } from "@/types/agentic-chat";

const THREADS_KEY_PREFIX = "athr.reader.ai.threads";
const ACTIVE_THREAD_KEY_PREFIX = "athr.reader.ai.active-thread";
const COMPOSER_DRAFT_KEY_PREFIX = "athr.reader.ai.composer-draft";

export type ReaderAiPersistedState = {
  threads: ReaderAiThread[];
  messagesByThread: Record<string, ReaderAiMessage[]>;
  threadSummariesByThread: Record<string, ReaderAiThreadSummary>;
  pinnedBlockIds: string[];
  savedAt: number;
};

const now = () => Date.now();

const makeThreadStorageKey = (resourceId: string) => `${THREADS_KEY_PREFIX}:${resourceId}`;
const makeActiveThreadStorageKey = (resourceId: string) => `${ACTIVE_THREAD_KEY_PREFIX}:${resourceId}`;
const makeComposerDraftKey = (resourceId: string) => `${COMPOSER_DRAFT_KEY_PREFIX}:${resourceId}`;

const normalizeThread = (thread: ReaderAiThread): ReaderAiThread => ({
  ...thread,
  scope: thread.scope === "workspace" ? "workspace" : "resource",
  archived: Boolean(thread.archived),
  pinned: Boolean(thread.pinned),
  archivedAt: typeof thread.archivedAt === "number" ? thread.archivedAt : thread.archived ? thread.updatedAt ?? now() : null,
  memoryMode: thread.memoryMode === "temporary" ? "temporary" : "default",
  artifactCount: typeof thread.artifactCount === "number" ? Math.max(0, thread.artifactCount) : 0,
  lastArtifactAt: typeof thread.lastArtifactAt === "number" ? thread.lastArtifactAt : null,
  branchSourceThreadId: typeof thread.branchSourceThreadId === "string" ? thread.branchSourceThreadId : null,
  referenceMode: thread.referenceMode ?? "none",
});

const normalizePersistedState = (data: Partial<ReaderAiPersistedState> | null | undefined): ReaderAiPersistedState => ({
  threads: Array.isArray(data?.threads) ? data.threads.map(normalizeThread) : [],
  messagesByThread: data?.messagesByThread && typeof data.messagesByThread === "object" ? data.messagesByThread : {},
  threadSummariesByThread:
    data?.threadSummariesByThread && typeof data.threadSummariesByThread === "object" ? data.threadSummariesByThread : {},
  pinnedBlockIds: Array.isArray(data?.pinnedBlockIds) ? data.pinnedBlockIds : [],
  savedAt: typeof data?.savedAt === "number" ? data.savedAt : now(),
});

const readJson = <T>(key: string): T | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: unknown) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // best effort local persistence only
  }
};

const genId = (prefix: string) => `${prefix}-${now()}-${Math.random().toString(36).slice(2, 8)}`;

const DEFAULT_THREAD_TITLE = "Study chat";
const PAGE_THREAD_TITLE_REGEX = /^Page \d+ chat$/i;

const normalizeTitleSeed = (value: string) =>
  value
    .replace(/\s+/g, " ")
    .replace(/^[`"'([{]+|[`"')\]}]+$/g, "")
    .trim();

const stripPromptPrefix = (value: string) =>
  value
    .replace(/^(please\s+)?(can you|could you|would you|help me|i need help with|tell me about)\s+/i, "")
    .replace(/^(explain|summarize|review|clarify|simplify|quiz me on|make an mcq about|create an essay on)\s+/i, "")
    .replace(/^(what is|what are|how do i|how can i|why does|why do)\s+/i, "");

const trimTitleLength = (value: string, maxLength = 44) => {
  if (value.length <= maxLength) return value;
  const sliced = value.slice(0, maxLength);
  const safeBoundary = sliced.lastIndexOf(" ");
  return `${(safeBoundary > 18 ? sliced.slice(0, safeBoundary) : sliced).trim()}…`;
};

export const isAutoReaderAiThreadTitle = (thread: ReaderAiThread) =>
  thread.title === DEFAULT_THREAD_TITLE || PAGE_THREAD_TITLE_REGEX.test(thread.title);

export const deriveReaderAiThreadTitle = ({
  prompt,
  topicLabel,
  pageNumber,
}: {
  prompt?: string | null;
  topicLabel?: string | null;
  pageNumber?: number;
}) => {
  const promptSeed = prompt ? trimTitleLength(stripPromptPrefix(normalizeTitleSeed(prompt)).replace(/[?.!,:;]+$/g, "")) : "";
  const topicSeed = topicLabel ? trimTitleLength(normalizeTitleSeed(topicLabel).replace(/[?.!,:;]+$/g, "")) : "";
  const title = promptSeed || topicSeed;
  if (title) return title;
  return pageNumber ? `Page ${pageNumber}` : DEFAULT_THREAD_TITLE;
};

export const createReaderAiThread = (
  resourceId: string,
  pageNumber?: number,
  productMode?: ToofiProductMode | null,
): ReaderAiThread => ({
  id: genId("thread"),
  resourceId,
  title: pageNumber ? `Page ${pageNumber} chat` : DEFAULT_THREAD_TITLE,
  createdAt: now(),
  updatedAt: now(),
  lastPageNumber: pageNumber,
  archived: false,
  messageCount: 0,
  summaryVersion: 0,
  lastTopicLabel: null,
  warnedLongThreadAt: null,
  currentProductMode: productMode ?? null,
  referenceMode: "none",
  pinned: false,
  archivedAt: null,
  memoryMode: "default",
  artifactCount: 0,
  lastArtifactAt: null,
  branchSourceThreadId: null,
});

export const readReaderAiState = (resourceId: string): ReaderAiPersistedState => {
  const data = readJson<ReaderAiPersistedState>(makeThreadStorageKey(resourceId));
  return normalizePersistedState(data);
};

export const writeReaderAiState = (resourceId: string, state: ReaderAiPersistedState) => {
  writeJson(makeThreadStorageKey(resourceId), { ...state, savedAt: now() });
};

export const syncThreadsToServer = async (resourceId: string, state: ReaderAiPersistedState): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from("reader_ai_thread_sync").upsert(
      {
        student_id: user.id,
        resource_id: resourceId,
        thread_data: state,
        synced_at: new Date().toISOString(),
      },
      { onConflict: "student_id,resource_id" },
    );
  } catch {
    // best effort sync only
  }
};

export const hydrateThreadsFromServer = async (resourceId: string): Promise<ReaderAiPersistedState | null> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from("reader_ai_thread_sync")
      .select("thread_data, synced_at")
      .eq("student_id", user.id)
      .eq("resource_id", resourceId)
      .maybeSingle();

    if (error || !data) return null;

    const storedLocal = readJson<ReaderAiPersistedState>(makeThreadStorageKey(resourceId));
    const localSavedAt = typeof storedLocal?.savedAt === "number" ? storedLocal.savedAt : 0;
    const serverState = normalizePersistedState((data.thread_data as Partial<ReaderAiPersistedState> | null) ?? null);
    const serverSavedAt =
      typeof (data.thread_data as Partial<ReaderAiPersistedState> | null)?.savedAt === "number"
        ? ((data.thread_data as Partial<ReaderAiPersistedState>).savedAt as number)
        : data.synced_at
          ? new Date(data.synced_at).getTime()
          : 0;

    if (serverSavedAt <= localSavedAt) {
      return null;
    }

    const hydratedAt = now();
    const hydratedState = {
      ...serverState,
      savedAt: hydratedAt,
    };

    writeReaderAiState(resourceId, hydratedState);
    return hydratedState;
  } catch {
    return null;
  }
};

export const readActiveThreadId = (resourceId: string): string | null =>
  readJson<string>(makeActiveThreadStorageKey(resourceId));

export const writeActiveThreadId = (resourceId: string, threadId: string | null) => {
  if (threadId) {
    writeJson(makeActiveThreadStorageKey(resourceId), threadId);
    return;
  }
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(makeActiveThreadStorageKey(resourceId));
  } catch {
    // ignore
  }
};

export const readComposerDraft = (resourceId: string): string =>
  readJson<string>(makeComposerDraftKey(resourceId)) ?? "";

export const writeComposerDraft = (resourceId: string, draft: string) => {
  writeJson(makeComposerDraftKey(resourceId), draft);
};

export const appendMessageToThread = (
  state: ReaderAiPersistedState,
  threadId: string,
  message: ReaderAiMessage,
) => {
  const existing = state.messagesByThread[threadId] ?? [];
  const nextMessages = [...existing, message];
  const messageCount = nextMessages.length;
  let warning: "none" | "approaching_limit" | "at_limit" = "none";
  if (messageCount >= 380) warning = "at_limit";
  else if (messageCount >= 300) warning = "approaching_limit";

  const nextThreads = state.threads.map((thread) =>
    thread.id === threadId
      ? {
          ...thread,
          updatedAt: now(),
          messageCount,
          warnedLongThreadAt: warning !== "none" && !thread.warnedLongThreadAt ? now() : thread.warnedLongThreadAt,
        }
      : thread,
  );
  return {
    state: {
      ...state,
      threads: nextThreads,
      messagesByThread: {
        ...state.messagesByThread,
        [threadId]: nextMessages.slice(-400),
      },
      savedAt: now(),
    },
    warning,
  };
};

export const createUserMessage = (threadId: string, text: string, blockType: ReaderAiBlock["type"] = "markdown"): ReaderAiMessage => ({
  id: genId("msg"),
  threadId,
  role: "user",
  createdAt: now(),
  blocks: blockType === "markdown" ? [{ type: "markdown", markdown: text }] : [{ type: "feedback", kind: "hint", text }],
});

export const createAssistantMessage = (
  threadId: string,
  blocks: ReaderAiBlock[],
  meta?: Partial<
    Pick<
      ReaderAiMessage,
      "actionType" | "source" | "sourceOrigin" | "suggestions" | "memoryUsage" | "learningMode" | "productMode" | "productIntent" | "runtimeMeta"
    >
  >,
): ReaderAiMessage => ({
  id: genId("msg"),
  threadId,
  role: "assistant",
  createdAt: now(),
  blocks,
  ...(meta ?? {}),
});

export const createSystemMessage = (threadId: string, text: string): ReaderAiMessage => ({
  id: genId("msg"),
  threadId,
  role: "system",
  createdAt: now(),
  blocks: [{ type: "feedback", kind: "hint", text }],
});

export const createEmptyThreadSummary = (threadId: string): ReaderAiThreadSummary => ({
  threadId,
  rollingSummary: "",
  activeGoals: [],
  openLoops: [],
  lastUpdatedAt: now(),
  summaryVersion: 0,
});
